import { Box } from '@mui/material';
import { Calculator, Wand2 } from 'lucide-react';
import { useRef } from 'react';
import { KitButton } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitAccordion,
    KitDropzone,
    KitField,
    KitFormBar,
    KitGrid2,
    KitInput,
    KitInputButton,
    KitInputIco,
    KitSelect,
    KitSwitch,
    KitTextarea,
} from '../common/MobileKitExtras';

/**
 * Formulário de produto no shell mobile — `prototipos-mobile/produtos/form.html`.
 * Só apresentação: valores, validação (Yup/Formik), cálculo de preço, marketplace e envio
 * vêm de `pages/products/Form.js`.
 */

const PackageIcon = protoIcon('package');
const DollarIcon = protoIcon('dollar');
const ImageIcon = protoIcon('image');
const GlobeIcon = protoIcon('globe');
const ScanIcon = protoIcon('scan');
const CameraIcon = protoIcon('camera');
const XIcon = protoIcon('x');

// Atalho de cadastro rápido abaixo do select (`+ Nova categoria`) — extra do app real.
function InlineLink({ label, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', mt: '6px', p: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
        >
            + {label}
        </Box>
    );
}

export default function MobileProductForm({
    formik,
    isEditing,
    categories,
    subCategories,
    suppliers,
    onChangeCategory,
    onChangeSubCategory,
    onChangeSupplier,
    onNewCategory,
    onNewSubCategory,
    onCodeBlur,
    onScan,
    onSuggestCode,
    onChangeCost,
    onBlurCost,
    onChangeSalePrice,
    onOpenCalculator,
    marginValue,
    marginHelp,
    belowExpected,
    adjustedSalePriceText,
    expectedMargin,
    onAdjustPrice,
    onToggleGeneric,
    showMarketplace,
    marketplaceChecked,
    marketplaceDisabled,
    marketplaceUrl,
    onToggleMarketplace,
    previewImage,
    onPickImage,
    onRemoveImage,
    isSectionOpen,
    onToggleSection,
    loading,
    onCancel,
    onSave,
}) {
    const fileRef = useRef(null);
    const { values, touched, errors } = formik;
    const errorOf = (name) => (touched[name] && errors[name]) || '';
    const imageSrc = previewImage || values.image;

    const field = (name, label, input, options = {}) => (
        <KitField label={label} required={options.required} help={errorOf(name) || options.help} error={Boolean(errorOf(name))}>
            {input}
        </KitField>
    );

    const textInput = (name, props = {}) => (
        <KitInput
            name={name}
            value={values[name] ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(errorOf(name))}
            {...props}
        />
    );

    const marketplaceDescription = marketplaceDisabled
        ? 'Preencha "Informações básicas" e "Preço e estoque" primeiro'
        : (marketplaceUrl ? (
            <Box component="a" href={marketplaceUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                Ver anúncio no Mercado Livre
            </Box>
        ) : 'Sincroniza preço e estoque');

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitAccordion
                open={isSectionOpen('info')}
                icon={PackageIcon}
                title="Informações básicas"
                subtitle="Nome, código, categoria e fornecedor"
                onToggle={() => onToggleSection('info')}
            >
                {field('name', 'Nome', textInput('name', { type: 'text', placeholder: 'Ex.: Óleo 5W30 1L' }), { required: true })}

                {field('code', 'Código do produto', (
                    <KitInputIco
                        icon={ScanIcon}
                        rightButtons={(
                            <>
                                <KitInputButton aria-label="Sugerir código do produto" onClick={onSuggestCode}><Wand2 size={20} /></KitInputButton>
                                <KitInputButton aria-label="Ler código de barras" onClick={onScan}><ScanIcon size={20} /></KitInputButton>
                            </>
                        )}
                    >
                        {textInput('code', { type: 'text', onBlur: onCodeBlur, sx: { pl: '44px', pr: '86px' } })}
                    </KitInputIco>
                ), { required: true })}

                <KitGrid2>
                    <Box>
                        {field('category', 'Categoria', (
                            <KitSelect
                                name="category"
                                value={values.category ?? ''}
                                onChange={(e) => onChangeCategory(e.target.value)}
                                onBlur={formik.handleBlur}
                                sx={errorOf('category') ? { borderColor: 'error.main' } : undefined}
                            >
                                <option value="" disabled>Selecione</option>
                                {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </KitSelect>
                        ), { required: true })}
                        <Box sx={{ mt: '-8px', mb: '14px' }}><InlineLink label="Nova categoria" onClick={onNewCategory} /></Box>
                    </Box>
                    <Box>
                        {field('subcategory', 'Subcategoria', (
                            <KitSelect name="subcategory" value={values.subcategory ?? ''} onChange={(e) => onChangeSubCategory(e.target.value)}>
                                <option value="">—</option>
                                {subCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </KitSelect>
                        ))}
                        <Box sx={{ mt: '-8px', mb: '14px' }}><InlineLink label="Nova subcategoria" onClick={onNewSubCategory} /></Box>
                    </Box>
                </KitGrid2>

                {field('supplier', 'Fornecedor', (
                    <KitSelect name="supplier" value={values.supplier ?? ''} onChange={(e) => onChangeSupplier(e.target.value)}>
                        <option value="">—</option>
                        {suppliers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </KitSelect>
                ))}
            </KitAccordion>

            <KitAccordion
                open={isSectionOpen('price')}
                icon={DollarIcon}
                title="Preço e estoque"
                subtitle="Custo, margem e quantidade disponível"
                onToggle={() => onToggleSection('price')}
            >
                <KitGrid2>
                    {field('cost', 'Custo (R$)', textInput('cost', { type: 'text', inputMode: 'numeric', onChange: onChangeCost, onBlur: onBlurCost }))}
                    <KitField label="Margem (%)">
                        <KitInput type="text" value={marginValue} disabled readOnly aria-label="Margem calculada" />
                    </KitField>
                </KitGrid2>

                {field('sale_price', 'Preço de venda', (
                    <KitInputIco rightButtons={<KitInputButton aria-label="Calcular Preço de Venda" onClick={onOpenCalculator}><Calculator size={20} /></KitInputButton>}>
                        <KitInput
                            name="sale_price"
                            type="text"
                            inputMode="numeric"
                            value={values.sale_price ? `R$ ${values.sale_price}` : ''}
                            placeholder="R$ 0,00"
                            onChange={onChangeSalePrice}
                            onBlur={formik.handleBlur}
                            error={Boolean(errorOf('sale_price'))}
                            sx={{ pr: '48px' }}
                        />
                    </KitInputIco>
                ), { required: true, help: marginHelp })}
                {belowExpected && (
                    <Box
                        component="button"
                        type="button"
                        onClick={onAdjustPrice}
                        sx={{ mt: '-8px', mb: '14px', ml: '2px', p: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: '#b45309', textDecoration: 'underline', cursor: 'pointer', textAlign: 'left' }}
                    >
                        {`Ajustar para R$ ${adjustedSalePriceText} (margem de ${expectedMargin}%)`}
                    </Box>
                )}

                <KitGrid2>
                    {field('quantity', 'Estoque atual', textInput('quantity', { type: 'number', inputMode: 'numeric', min: 0, disabled: Boolean(values.is_generic || isEditing) }))}
                    {field('min_quantity', 'Estoque mínimo', textInput('min_quantity', { type: 'number', inputMode: 'numeric', min: 0, disabled: Boolean(values.is_generic) }))}
                </KitGrid2>
            </KitAccordion>

            <KitAccordion
                open={isSectionOpen('media')}
                icon={ImageIcon}
                title="Imagem do produto"
                subtitle="Foto exibida no sistema e nas vendas"
                onToggle={() => onToggleSection('media')}
            >
                <Box
                    component="input"
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                        const file = event.target.files && event.target.files[0];
                        if (file) onPickImage(file);
                        event.target.value = '';
                    }}
                />
                {imageSrc ? (
                    <KitDropzone height={200} onClick={() => fileRef.current && fileRef.current.click()} aria-label="Trocar foto" sx={{ borderStyle: 'solid' }}>
                        <Box component="img" src={imageSrc} alt="Produto" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <Box
                            component="span"
                            role="button"
                            aria-label="Remover foto"
                            onClick={(e) => { e.stopPropagation(); onRemoveImage(); }}
                            sx={{ position: 'absolute', top: 8, right: 8, width: 40, height: 40, borderRadius: '12px', backgroundColor: 'rgba(0,0,0,.55)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <XIcon size={20} />
                        </Box>
                    </KitDropzone>
                ) : (
                    <KitDropzone icon={CameraIcon} onClick={() => fileRef.current && fileRef.current.click()}>
                        Tirar foto ou escolher da galeria
                    </KitDropzone>
                )}
                <Box sx={{ height: '8px' }} />
            </KitAccordion>

            <KitAccordion
                open={isSectionOpen('market')}
                icon={GlobeIcon}
                title="Marketplace"
                subtitle="Sincronizar com Mercado Livre"
                onToggle={() => onToggleSection('market')}
            >
                {showMarketplace && (
                    <KitSwitch
                        title="Publicar no Mercado Livre"
                        description={marketplaceDescription}
                        checked={marketplaceChecked}
                        onChange={onToggleMarketplace}
                        disabled={marketplaceDisabled}
                    />
                )}
                <KitSwitch
                    title="Produto genérico"
                    description={values.is_generic ? 'Sem controle de estoque' : ''}
                    checked={Boolean(values.is_generic)}
                    onChange={(e) => onToggleGeneric(e.target.checked)}
                    disabled={isEditing}
                />
                {field('description', 'Descrição (opcional)', (
                    <KitTextarea
                        name="description"
                        value={values.description ?? ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        maxLength={100}
                        sx={errorOf('description') ? { borderColor: 'error.main' } : undefined}
                    />
                ), { help: `${(values.description || '').length}/100` })}
            </KitAccordion>

            <KitFormBar>
                <KitButton onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onSave} disabled={loading}>
                    {loading ? 'Salvando...' : (isEditing ? 'Atualizar produto' : 'Salvar produto')}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}
